// current weather conditions display
import STATUS from './status.mjs';
import { DateTime } from '../vendor/auto/luxon.mjs';
import WeatherDisplay from './weatherdisplay.mjs';
import { registerDisplay, timeZone } from './navigation.mjs';
import setTiles from './radar-tiles.mjs';
import { getXYFromLatitudeLongitudeMap } from './radar-utils.mjs';
import { getFrames, buildTileGrid, stitchTiles } from './rainviewer.mjs';

class Radar extends WeatherDisplay {
	constructor(navId, elemId) {
		super(navId, elemId, 'Local Radar');

		this.okToDrawCurrentConditions = false;
		this.okToDrawCurrentDateTime = false;

		// set max images
		this.dopplerRadarImageMax = 6;
		// update timing
		this.timing.baseDelay = 350;
		this.timing.delay = [
			{ time: 4, si: 5 },
			{ time: 1, si: 0 },
			{ time: 1, si: 1 },
			{ time: 1, si: 2 },
			{ time: 1, si: 3 },
			{ time: 1, si: 4 },
			{ time: 4, si: 5 },
			{ time: 1, si: 0 },
			{ time: 1, si: 1 },
			{ time: 1, si: 2 },
			{ time: 1, si: 3 },
			{ time: 1, si: 4 },
			{ time: 4, si: 5 },
			{ time: 1, si: 0 },
			{ time: 1, si: 1 },
			{ time: 1, si: 2 },
			{ time: 1, si: 3 },
			{ time: 1, si: 4 },
			{ time: 12, si: 5 },
		];
	}

	async getData(weatherParameters, refresh) {
		if (!super.getData(weatherParameters, refresh)) return;

		if (this.weatherParameters.state === 'AK' || this.weatherParameters.state === 'HI') {
			this.setStatus(STATUS.noData);
			return;
		}

		const { latitude: lat, longitude: lon } = this.weatherParameters;

		// set up static base map tiles (unchanged from original)
		const sourceXY = getXYFromLatitudeLongitudeMap(this.weatherParameters);
		setTiles({ sourceXY, elemId: this.elemId });

		try {
			const result = await getFrames();
			if (!result) throw new Error('RainViewer API returned no data');

			const { frames } = result;

			const radarInfo = (await Promise.all(frames.map(async (frame) => {
				const tileGrid = buildTileGrid(frame.path, lat, lon);
				const dataURL = await stitchTiles(tileGrid);
				if (!dataURL) return null; // all tiles failed for this frame

				const time = DateTime.fromSeconds(frame.time, { zone: 'UTC' }).setZone(timeZone());
				const elem = this.fillTemplate('frame', { map: { type: 'img', src: dataURL } });
				return { time, elem };
			}))).filter(Boolean);

			if (radarInfo.length === 0) throw new Error('No radar frames loaded');

			const scrollArea = this.elem.querySelector('.scroll-area');
			scrollArea.replaceChildren(...radarInfo.map((r) => r.elem));

			this.times = radarInfo.map((r) => r.time);
			// Clamp screenIndexes so drawCanvas never accesses this.times out of bounds
			// if fewer than 6 frames loaded. calcNavTiming already ran in super.getData().
			const maxSi = radarInfo.length - 1;
			this.timing.screenIndexes = this.timing.screenIndexes.map((si) => Math.min(si, maxSi));
			this.setStatus(STATUS.loaded);
		} catch (_error) {
			this.timing.totalScreens = 0;
			if (this.isEnabled) this.setStatus(STATUS.failed);
		}
	}

	async drawCanvas() {
		super.drawCanvas();
		const frameIndex = Math.min(this.screenIndex, this.times.length - 1);
		const time = this.times[frameIndex].toLocaleString(DateTime.TIME_SIMPLE);
		const timePadded = time.length >= 8 ? time : `&nbsp;${time} `;
		this.elem.querySelector('.header .right .time').innerHTML = timePadded;

		// get image offset calculation
		// is slides slightly because of scaling so we have to take a measurement from the rendered page
		const actualFrameHeight = this.elem.querySelector('.frame').scrollHeight;

		// scroll to image
		this.elem.querySelector('.scroll-area').style.top = `${-frameIndex * actualFrameHeight}px`;

		this.finishDraw();
	}
}

// register display
registerDisplay(new Radar(11, 'radar'));
